import { useState } from 'react';
import { useFrameworkChangeSet, useApproveChangeSet, useRejectChangeSet, useDiscardChangeSet } from '@/hooks/useFrameworkChanges';
import { useHasPermission } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { ChangeItemRow } from './ChangeItemRow';

export function ChangeSetDetailView({ setId }: { setId: string }) {
  const { data, isLoading } = useFrameworkChangeSet(setId);
  const approveChangeSet = useApproveChangeSet();
  const rejectChangeSet = useRejectChangeSet();
  const discardChangeSet = useDiscardChangeSet();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const { changeSet, items, impact } = data;
  const counts = items.reduce(
    (acc, it) => {
      acc[it.changeType] = (acc[it.changeType] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const handleApprove = async () => {
    try {
      await approveChangeSet.mutateAsync(setId);
      setShowApproveConfirm(false);
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed to approve: ${err.message || 'unknown error'}`);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('A rejection reason is required');
      return;
    }
    try {
      await rejectChangeSet.mutateAsync({ id: setId, reason: rejectReason.trim() });
      setShowRejectModal(false);
      setRejectReason('');
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed to reject: ${err.message || 'unknown error'}`);
    }
  };

  const canReview = changeSet.status === 'submitted';
  const canApprove = useHasPermission('framework.change_sets.approve');
  const canReject = useHasPermission('framework.change_sets.reject');

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900">
            {changeSet.title || '(Untitled change set)'}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            <span className="font-mono">{changeSet.id}</span> · by {changeSet.createdBy} ·{' '}
            <span className={cn(
              'inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase',
              changeSet.status === 'open' && 'bg-gray-100 text-gray-700',
              changeSet.status === 'submitted' && 'bg-amber-100 text-amber-800',
              changeSet.status === 'approved' && 'bg-green-100 text-green-800',
              changeSet.status === 'rejected' && 'bg-red-100 text-red-800',
            )}>
              {changeSet.status}
            </span>
          </p>
          {changeSet.description && (
            <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{changeSet.description}</p>
          )}
          {changeSet.rejectionReason && (
            <div className="mt-2 rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-900">
              <span className="font-medium">Previously rejected:</span> {changeSet.rejectionReason}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            Pending changes ({items.length})
            {counts.create ? <span className="ml-2 text-[10px] text-green-700 bg-green-50 px-1.5 py-0.5 rounded">+{counts.create} create</span> : null}
            {counts.update ? <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">~{counts.update} update</span> : null}
            {counts.delete ? <span className="ml-2 text-[10px] text-red-700 bg-red-50 px-1.5 py-0.5 rounded">−{counts.delete} delete</span> : null}
          </h4>
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">No items in this change set</p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <ChangeItemRow key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Impact</h4>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-1.5">
            <div>
              <span className="font-medium text-gray-900">{impact.draftProjectsAffected.length}</span>{' '}
              draft project(s) will be modified on approval:
              {impact.draftProjectsAffected.length > 0 && (
                <ul className="ml-4 mt-1 list-disc text-gray-700">
                  {impact.draftProjectsAffected.slice(0, 8).map((p) => (
                    <li key={p.id}>{p.name}</li>
                  ))}
                  {impact.draftProjectsAffected.length > 8 && (
                    <li className="text-gray-500">…and {impact.draftProjectsAffected.length - 8} more</li>
                  )}
                </ul>
              )}
            </div>
            <div>
              <span className="font-medium text-gray-900">{impact.lockedProjectsCount}</span> locked
              project(s) (submitted, approved, archived) — unaffected, they use snapshots
            </div>
          </div>
        </div>
      </div>

      {/* Discard button for stale open change sets */}
      {changeSet.status === 'open' && (
        <div className="border-t border-gray-200 p-3 bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={async () => {
              if (!confirm(`Discard this open change set "${changeSet.title || '(Untitled)'}"? This cannot be undone.`)) return;
              try {
                await discardChangeSet.mutateAsync(changeSet.id);
              } catch (err: unknown) {
                const e = err as { message?: string };
                alert(`Failed to discard: ${e.message || 'unknown error'}`);
              }
            }}
            disabled={discardChangeSet.isPending}
            className="btn-secondary text-red-600 hover:bg-red-50 text-sm"
          >
            {discardChangeSet.isPending ? 'Discarding…' : 'Discard'}
          </button>
        </div>
      )}

      {canReview && (canApprove || canReject) && (
        <div className="border-t border-gray-200 p-3 bg-gray-50 flex items-center justify-end gap-2">
          {canReject && (
            <button onClick={() => setShowRejectModal(true)} className="btn-secondary text-red-600 hover:bg-red-50 text-sm">
              Reject
            </button>
          )}
          {canApprove && (
            <button onClick={() => setShowApproveConfirm(true)} className="btn-primary text-sm flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Approve & Publish
            </button>
          )}
        </div>
      )}

      {/* Reject modal */}
      {showRejectModal && (
        <div className="dialog-overlay">
          <div className="dialog max-w-md">
            <div className="dialog-header">
              <h2 className="dialog-title">Reject change set</h2>
            </div>
            <div className="dialog-body space-y-4">
              <p className="text-sm text-gray-600">
                The editor will see this reason and can revise + resubmit.
              </p>
              <div>
                <label className="label">Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="input mt-1 w-full"
                  rows={4}
                  autoFocus
                />
              </div>
            </div>
            <div className="dialog-footer">
              <button onClick={() => setShowRejectModal(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={handleReject}
                className="btn-primary text-red-600"
                disabled={rejectChangeSet.isPending || !rejectReason.trim()}
              >
                {rejectChangeSet.isPending ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve confirmation modal */}
      {showApproveConfirm && (
        <div className="dialog-overlay">
          <div className="dialog max-w-md">
            <div className="dialog-header">
              <h2 className="dialog-title">Publish {items.length} change{items.length === 1 ? '' : 's'}?</h2>
            </div>
            <div className="dialog-body space-y-4">
              <p className="text-sm text-gray-700">This will:</p>
              <ul className="list-disc ml-5 text-sm text-gray-700 space-y-1">
                <li>Apply all changes to the live framework</li>
                <li>Cascade deletions through {impact.draftProjectsAffected.length} draft project(s)</li>
                <li>Create a new framework version</li>
              </ul>
              <p className="text-xs text-gray-500">
                Locked projects (submitted, approved, archived) are unaffected — they use snapshots.
                This cannot be undone except by a new approved change set.
              </p>
            </div>
            <div className="dialog-footer">
              <button onClick={() => setShowApproveConfirm(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={handleApprove}
                className="btn-primary"
                disabled={approveChangeSet.isPending}
              >
                {approveChangeSet.isPending ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
